import {useContext, useEffect, useState} from "react";
import Grid from '@mui/material/Grid2';
import {Box, Typography} from "@mui/material";
import {handleInputChangeSelect} from "../../../../utils/formUtil";
import {WorkSortOrderValue} from "../../../../constants/constants";
import {SearchCond, useSearchFormProps} from "../forms/SearchForm";
import {SortOrderProps} from "../forms/SortOrder";
import {findWork, FindWorkResponseData} from "../../../../utils/awsAmplifyUtil";
import {
  CONTEXT_ACTION_TYPE,
  DataStoreContext,
  STEPS,
  WorkClassSelectionStepPayload
} from "../../contexts/HandSelectionContext";
import WorkClassListItem from "../lists/WorkClassListItem";
import {WorkClassDocument} from "../../../../types/pj1dbApiWork";
import {WORK_ATTRS} from "../../../../constants/work";
import {FormWorkSearchCond, workAttrList} from "../../../workdb/types/workForm";
import useFetchAllQuery, {FetchMessage, Status, useFetchAllQueryHelper} from "../../../../hooks/useFetchAllQuery";
import {convertToWorkDocuments, excludeClassesWithChildren, sortWorkClass} from "../../../../utils/workUtil";
import useRepresentativeModel from "../../../workdb/hooks/useRepresentativeModel";
import {FindModelResponse} from "../../../../types/pj1dbApiHandling";

export interface WorkClassSelectionStepProps {
  workClasses: WorkClassDocument[];
  onSelectClass: (work: WorkClassDocument) => void;
  searchStatus: Status;
  isLoadingRepresentativeModel: boolean
  models: FindModelResponse[];
}

export default function WorkClassSelectionStep(props: WorkClassSelectionStepProps) {
  return (
    <>
      <Grid container alignItems="stretch" spacing={2} sx={{mt: 2}}>
        {props.workClasses.map(workClass => (
          <Grid
            key={workClass.attr[WORK_ATTRS.classId.key]}
            onClick={() => props.onSelectClass(workClass)}
          >
            <WorkClassListItem work={workClass} representativeModels={props.models} isLoading={props.isLoadingRepresentativeModel}/>
          </Grid>
        ))}
      </Grid>
      <FetchMessage data={props.workClasses} status={props.searchStatus}/>
    </>
  );
}

export function useWorkClassSelectionStepHelpers() {
  const {state, dispatch} = useContext(DataStoreContext);
  const [workClassList, setWorkClassList] = useState<WorkClassDocument[]>(
    sortWorkClass(
      excludeClassesWithChildren(convertToWorkDocuments(state.workClassSelectionStep.workClassList)),
      state.workClassSelectionStep.searchCond.sortOrder
    )
  );
  const {searchCond, setSearchCond} = useSearchFormProps();
  const {status, data, fetchAll} = useWorkClasses({searchCond});
  const {searchStatus} = useFetchAllQueryHelper({
    onChange: (dispatchData, dispatchStatus) => {
      dispatch({
        type: CONTEXT_ACTION_TYPE.WORK_CLASS_SELECTION_STEP, payload: {
          ...state.workClassSelectionStep,
          workClassList: dispatchData,
          searchStatus: dispatchStatus,
        } satisfies WorkClassSelectionStepPayload
      });
    },
    data,
    status,
  });

  const changeSortOrder: SortOrderProps['onChange'] = (event) => {
    handleInputChangeSelect<SearchCond>(event, "sortOrder", searchCond, setSearchCond);
    setWorkClassList(sortWorkClass(workClassList, event.target.value as WorkSortOrderValue));
    dispatch({
      type: CONTEXT_ACTION_TYPE.WORK_CLASS_SELECTION_STEP, payload: {
        ...state.workClassSelectionStep,
        searchCond: {...searchCond, sortOrder: event.target.value as WorkSortOrderValue},
      } satisfies WorkClassSelectionStepPayload
    });
  }

  const selectClass = (work: WorkClassDocument) => {
    dispatch({
      type: CONTEXT_ACTION_TYPE.WORK_CLASS_SELECTION_STEP, payload: {
        ...state.workClassSelectionStep,
        selectedWorkClass: work,
        step: STEPS.WORK_INSTANCE_SELECTION,
      } satisfies WorkClassSelectionStepPayload
    });
  }

  // 検索済みデータの復元
  useEffect(() => {
    setSearchCond(state.workClassSelectionStep.searchCond);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setWorkClassList(sortWorkClass(
      excludeClassesWithChildren(convertToWorkDocuments(state.workClassSelectionStep.workClassList)),
      state.workClassSelectionStep.searchCond.sortOrder
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.workClassSelectionStep.workClassList]);

  const fetchWorkClasses = async () => {
    dispatch({
      type: CONTEXT_ACTION_TYPE.WORK_CLASS_SELECTION_STEP, payload: {
        ...state.workClassSelectionStep,
        workClassList: [],
        step: STEPS.WORK_CLASS_SELECTION,
      } satisfies WorkClassSelectionStepPayload
    });
    await fetchAll();
  }

  const {models, isLoading: isLoadingRepresentativeModel} = useRepresentativeModel({
    workClasses: workClassList,
    acquiredRepresentativeModels: state.workClassSelectionStep.workModels,
  });
  useEffect(() => {
    dispatch({
      type: CONTEXT_ACTION_TYPE.WORK_CLASS_SELECTION_STEP, payload: {
        ...state.workClassSelectionStep,
        workModels: models,
      } satisfies WorkClassSelectionStepPayload
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models]);

  return {
    workClassList,
    searchCond,
    setSearchCond,
    changeSortOrder,
    selectClass,
    fetchWorkClasses,
    searchStatus,
    isLoadingRepresentativeModel,
  }
}

function Message({children}: { children: React.ReactNode }) {
  return (
    <Box sx={{my: 3, width: "100%", textAlign: "center"}}>
      <Typography component='p' variant="subtitle1" sx={{fontWeight: "bold"}}>{children}</Typography>
    </Box>
  );
}

function useWorkClasses({searchCond}: { searchCond: FormWorkSearchCond }) {
  const queryResult = useFetchAllQuery<FindWorkResponseData<'class'>>({
    queryFn: (query) => {
      const params = {
        version: "work-user",
        queryAttr: {
          ...(searchCond.keyword === "" ? {} : {
            $or: [
              {[workAttrList.nameJp.key]: {"$regex": searchCond.keyword, "$options": "i"}},
              {[workAttrList.nameEn.key]: {"$regex": searchCond.keyword, "$options": "i"}},
              {[workAttrList.code.key]: {"$regex": searchCond.keyword, "$options": "i"}},
              {[workAttrList.manufacturer.key]: {"$regex": searchCond.keyword, "$options": "i"}},
            ]
          }),
        },
      };

      return findWork('class', params, query);
    },
    needToFetchMore: (data, limit) => Object.entries(data?.items ?? {}).length === limit,
  });

  return queryResult;
}
